import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { SubcategoryMaster, CategoryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const SubcategoryMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [subcategories, setSubcategories] = useState<SubcategoryMaster[]>([]);
  const [categories, setCategories] = useState<CategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<SubcategoryMaster | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [formData, setFormData] = useState({
    categoryId: '',
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  useEffect(() => {
    if (filterCategory) {
      fetchSubcategories();
    }
  }, [filterCategory]);

  const fetchCategories = async () => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'categories' });
      const data = (result.data as any).data as CategoryMaster[];
      setCategories(data);
    } catch (error: any) {
      onError(error.message || '業種マスタの取得に失敗しました');
    }
  };

  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'subcategories', includeInactive: true };
      if (filterCategory) {
        params.categoryId = filterCategory;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as SubcategoryMaster[];
      setSubcategories(data);
    } catch (error: any) {
      onError(error.message || '業種詳細マスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (subcategory?: SubcategoryMaster) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setFormData({
        categoryId: subcategory.categoryId,
        code: subcategory.code,
        nameJa: subcategory.name.ja,
        nameTh: subcategory.name.th || '',
        nameEn: subcategory.name.en || '',
        sortOrder: subcategory.sortOrder,
        isActive: subcategory.isActive,
      });
    } else {
      setEditingSubcategory(null);
      setFormData({
        categoryId: filterCategory || categories[0]?.id || '',
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        sortOrder: subcategories.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSubcategory(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.categoryId || !formData.code || !formData.nameJa) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingSubcategory) {
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingSubcategory.id,
          type: 'subcategories',
          data: {
            categoryId: formData.categoryId,
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('業種詳細マスタを更新しました');
      } else {
        const createSubcategory = httpsCallable(functions, 'createSubcategory');
        await createSubcategory({
          categoryId: formData.categoryId,
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('業種詳細マスタを作成しました');
      }

      handleCloseDialog();
      fetchSubcategories();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (subcategory: SubcategoryMaster) => {
    if (!window.confirm(`${subcategory.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: subcategory.id,
        type: 'subcategories',
      });
      onSuccess((result.data as any).message || '業種詳細マスタを削除しました');
      fetchSubcategories();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? `${category.icon} ${category.name.ja}` : categoryId;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            業種詳細を追加
          </Button>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>業種でフィルタ</InputLabel>
            <Select
              value={filterCategory}
              label="業種でフィルタ"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">全て</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.icon} {category.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>業種</TableCell>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subcategories.map((subcategory) => (
              <TableRow key={subcategory.id}>
                <TableCell>{getCategoryName(subcategory.categoryId)}</TableCell>
                <TableCell>{subcategory.code}</TableCell>
                <TableCell>{subcategory.name.ja}</TableCell>
                <TableCell>{subcategory.name.th || '-'}</TableCell>
                <TableCell>{subcategory.name.en || '-'}</TableCell>
                <TableCell>{subcategory.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={subcategory.isActive ? '有効' : '無効'}
                    color={subcategory.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(subcategory)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(subcategory)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSubcategory ? '業種詳細マスタを編集' : '業種詳細マスタを追加'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>業種</InputLabel>
            <Select
              value={formData.categoryId}
              label="業種"
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              disabled={!!editingSubcategory}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.icon} {category.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingSubcategory}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubcategoryMasterTab;
